import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ISchedule } from '../../../@academy/shared/services/building.service';

import { ApiService, ParamsObject } from '../../utils/api.service';
import { Building, BuildingOpeningDay, Room, Service } from '../../models';
import { map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

export interface RoomBookingReportingParams {
  date: string;
  format: string;
}

@Injectable()
export class BuildingService {

  path = 'buildings';

  constructor(
    private apiService: ApiService,
  ) {
  }

  create(building): Observable<Building> {
    return this.apiService.post(this.path, building);
  }

  delete(building) {
    return this.apiService.delete(this.path + '/' + building.id);
  }

  edit(building): Observable<Building> {
    return this.apiService.put(this.path + '/' + building.id, building);
  }

  getAll(alreadyAddedOnly: boolean = true): Observable<Building[]> {
    const httpParameters = new HttpParams({
      fromObject: {alreadyAddedOnly: `${alreadyAddedOnly}`},
    });

    return this.apiService.getSave(this.path, [httpParameters]);
  }

  getBuildingOpeningDays(id: string, params: HttpParams = new HttpParams()): Observable<BuildingOpeningDay[]> {
    return this.apiService.get(`${this.path}/${id}/opening_days`, params);
  }

  /**
   * Look for free rooms when creating/editing a course instance. Takes an array of schedule actions as a parameter
   *
   * @param buildingId Building identifier
   * @param scheduleActions Schedule actions wanted by the user
   */
  getCourseInstanceEmptyRooms(buildingId: string, scheduleActions: ISchedule[]): Observable<Room[]> {
    const lastSchedule = scheduleActions[scheduleActions.length - 1];
    const firstSchedule = scheduleActions[0];

    return this.getEmptyRooms(
      buildingId,
      new Date(firstSchedule.start),
      new Date(lastSchedule.end),
      firstSchedule.capacity,
    );
  }

  /**
   * Returns free rooms during a specified period
   *
   * @param buildingId Building where the search should be performed
   * @param startDate Start of desired period
   * @param endDate End of desired period
   * @param capacity Desired capacity
   * @param excludedBooking Room booking to exclude from already taken slots. To use when trying to move a room booking
   */
  getEmptyRooms(buildingId: string,
                startDate: Date,
                endDate: Date,
                capacity: number,
                excludedBooking?: number): Observable<Room[]> {
    let httpParams = new HttpParams({
      fromObject: {
        capacity: capacity.toString(),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });

    if (excludedBooking) {
      httpParams = httpParams.set('excludedBooking', `${excludedBooking}`);
    }

    return this.apiService.get(`${this.path}/${buildingId}/empty_rooms`, httpParams)
      .pipe(
        map(data => data.map(room => new Room(room))),
      );
  }

  getReporting(id: string, params: RoomBookingReportingParams) {
    const fromObject = {
      date: params.date,
      format: params.format,
    };

    const httpParameters = new HttpParams({
      fromObject,
    });

    return this.apiService.download(`${this.path}/${id}/room_bookings`, httpParameters);
  }

  public getBuildingRooms(id: string, params: ParamsObject = null):
    Observable<Room[]> {
    const httpParameters = new HttpParams({
      fromObject: params,
    });

    return this.apiService.getSave(`${this.path}/${id}/rooms`, [httpParameters, 'getBuildingRooms'])
      .pipe(
        map(data => data.map(room => new Room(room))),
      );
  }

  public getByOrganization(id: number): Observable<Building[]> {
    return this.apiService.getSave(`organizations/${id}/${this.path}`)
      .pipe(
        map(buildings => buildings.map(building => new Building(building))),
      );
  }

  public getServices(id: string, params: HttpParams = new HttpParams()):
    Observable<Service[]> {
    return this.apiService.getSave(`${this.path}/${id}/services`, [params, 'getServices']);
  }

  public get(id: string): Observable<Building> {
    return this.apiService.getSave(this.path + '/' + id)
      .pipe(
        map(data => new Building(data)),
      );
  }
}
